import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaSearch, FaTimes, FaTimesCircle } from 'react-icons/fa';
import style from './RentalFilters.module.css';

const DEFAULT_CONTRACT_STATUS = [
  { value: 'active', label: 'Activos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'expired', label: 'Vencidos' },
  { value: 'terminated', label: 'Cancelados' },
  { value: 'all', label: 'Todos' },
];

const toArray = (v) => {
  if (v == null || v === '') return [];
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string' && v.includes(',')) return v.split(',').filter(Boolean);
  return [String(v)];
};

/* ========== Hook: detectar click afuera ========== */
const useOutsideClose = (ref, onClose, isOpen) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && onClose());
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [ref, onClose, isOpen]);
};

/* ========== Subcomponente: dropdown con checkboxes ========== */
const MultiSelectDropdown = ({
  label,
  placeholder = 'Seleccionar...',
  options,
  values,
  onChange,
  searchable = true,
  emptyText = 'Sin opciones',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  useOutsideClose(wrapperRef, () => setOpen(false), open);

  const valueSet = useMemo(() => new Set(values.map(String)), [values]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const term = search.toLowerCase();
    return options.filter((o) => (o.label || '').toLowerCase().includes(term));
  }, [options, search]);

  const toggle = (value) => {
    const v = String(value);
    const next = valueSet.has(v) ? values.filter((x) => String(x) !== v) : [...values, v];
    onChange(next);
  };

  const removeChip = (value) => {
    onChange(values.filter((v) => String(v) !== String(value)));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedOptions = options.filter((o) => valueSet.has(String(o.value)));
  const summary = values.length === 0
    ? placeholder
    : values.length === 1
      ? (selectedOptions[0]?.label || values[0])
      : `${values.length} seleccionados`;

  return (
    <div className={style.field}>
      <label className={style.label}>{label}</label>
      <div className={style.dropdownWrap} ref={wrapperRef}>
        <button
          type="button"
          className={`${style.dropdownBtn} ${values.length ? style.dropdownBtnActive : ''}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={style.dropdownText}>{summary}</span>
          {values.length > 0 && (
            <span className={style.clearChip} onClick={clearAll} role="button" aria-label="Limpiar">
              <FaTimesCircle />
            </span>
          )}
          <FaChevronDown className={`${style.caret} ${open ? style.caretOpen : ''}`} />
        </button>

        {open && (
          <div className={style.dropdownPanel}>
            {searchable && (
              <div className={style.searchBox}>
                <FaSearch className={style.searchIcon} />
                <input
                  type="text"
                  autoFocus
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={style.searchInput}
                />
              </div>
            )}
            <div className={style.optionsList}>
              {filtered.length === 0 && (
                <div className={style.emptyOption}>{emptyText}</div>
              )}
              {filtered.map((opt) => {
                const checked = valueSet.has(String(opt.value));
                return (
                  <label key={opt.value} className={`${style.option} ${checked ? style.optionChecked : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className={style.chips}>
          {selectedOptions.map((opt) => (
            <span key={opt.value} className={style.chip}>
              {opt.label}
              <button
                type="button"
                className={style.chipRemove}
                onClick={() => removeChip(opt.value)}
                aria-label={`Quitar ${opt.label}`}
              >
                <FaTimes />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== Componente principal ========== */
const RentalFilters = ({
  value = {},
  onChange,
  onClear,
  showDateField = false,
  statusOptions = DEFAULT_CONTRACT_STATUS,
  sellers = [],
  properties = [],
  compact = false,
}) => {
  const sellerIdValues = toArray(value.sellerId);
  const propertyIdValues = toArray(value.propertyId);
  const statusValues = toArray(value.status);

  const sellerOptions = useMemo(() => sellers.map((s) => ({
    value: s.id,
    label: `${s.name || ''}${s.last_name ? ' ' + s.last_name : ''}`.trim() || `#${s.id}`,
  })), [sellers]);

  const propertyOptions = useMemo(() => properties.map((p) => ({
    value: p.id,
    label: p.title
      || `${p.street || ''}${p.number ? ' ' + p.number : ''}`.trim()
      || `#${p.id}`,
  })), [properties]);

  const update = (patch) => {
    const next = { ...value, ...patch };
    if (Object.prototype.hasOwnProperty.call(value, 'page')) next.page = 1;
    onChange(next);
  };

  const setMulti = (key, arr) => {
    update({ [key]: arr.length ? arr : undefined });
  };

  const clearAll = () => {
    if (onClear) onClear();
    else onChange({});
  };

  const activeCount = [
    sellerIdValues.length > 0,
    propertyIdValues.length > 0,
    statusValues.length > 0,
    !!value.currency,
    !!value.dateFrom,
    !!value.dateTo,
  ].filter(Boolean).length;

  return (
    <div className={style.panel}>
      <div className={style.header}>
        <h4 className={style.title}>
          Filtros{activeCount > 0 && <span className={style.badge}>{activeCount}</span>}
        </h4>
        <button type="button" className={style.clearBtn} onClick={clearAll} disabled={activeCount === 0}>
          Limpiar filtros
        </button>
      </div>

      <div className={style.grid}>
        <MultiSelectDropdown
          label="Agente"
          placeholder="Todos los agentes"
          options={sellerOptions}
          values={sellerIdValues}
          onChange={(arr) => setMulti('sellerId', arr)}
          emptyText="No hay agentes"
        />

        <MultiSelectDropdown
          label="Propiedad"
          placeholder="Todas las propiedades"
          options={propertyOptions}
          values={propertyIdValues}
          onChange={(arr) => setMulti('propertyId', arr)}
          emptyText="No hay propiedades en la lista actual"
        />

        <MultiSelectDropdown
          label="Estado"
          placeholder="Todos los estados"
          options={statusOptions}
          values={statusValues}
          onChange={(arr) => setMulti('status', arr)}
          searchable={false}
        />

        {!compact && (
          <div className={style.field}>
            <label className={style.label}>Moneda</label>
            <select
              value={value.currency || ''}
              onChange={(e) => update({ currency: e.target.value || undefined })}
              className={style.singleSelect}
            >
              <option value="">Todas</option>
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </select>
          </div>
        )}

        <div className={style.field}>
          <label className={style.label}>Desde</label>
          <input
            type="date"
            value={value.dateFrom || ''}
            onChange={(e) => update({ dateFrom: e.target.value || undefined })}
            className={style.dateInput}
          />
        </div>

        <div className={style.field}>
          <label className={style.label}>Hasta</label>
          <input
            type="date"
            value={value.dateTo || ''}
            onChange={(e) => update({ dateTo: e.target.value || undefined })}
            className={style.dateInput}
          />
        </div>

        {showDateField && (
          <div className={style.field}>
            <label className={style.label}>Filtrar por</label>
            <div className={style.segmented}>
              <button
                type="button"
                className={`${style.segBtn} ${(value.dateField || 'dueDate') === 'dueDate' ? style.segBtnActive : ''}`}
                onClick={() => update({ dateField: 'dueDate' })}
              >
                Vencimiento
              </button>
              <button
                type="button"
                className={`${style.segBtn} ${value.dateField === 'paymentDate' ? style.segBtnActive : ''}`}
                onClick={() => update({ dateField: 'paymentDate' })}
              >
                Cobro
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalFilters;
